import { retryIfNot404 } from "@src/global_functions/fortyTwoApi/reactQueryUtils";
import {
  recursiveCamelCaseCipher,
  recursiveSnakeCaseCipher,
} from "@src/global_functions/postgrestApi";
import {
  useMutation,
  useQueryClient,
  useQuery as useReactQuery,
} from "@tanstack/react-query";
import FetchClients from "../client";
import { PARKING_QUERY_KEYS } from "./detachedParking";
import {
  NotFoundError,
  SurfaceLot,
  SurfaceLotData,
  throwServiceError,
} from "./util";

const surfaceLot = {
  useQuery,
  useQueryAll,
  mutations: { useDelete, usePut },
} as const;

export default surfaceLot;

export const SURFACE_LOT_QUERY_KEY = "/surface_lot";

const getSurfaceLots = async (site_id: number) => {
  const { data, error } = await FetchClients.tenantsAndSpaces.GET(
    "/{site_id}/surface_lot",
    { params: { path: { site_id } } }
  );
  if (data) return recursiveCamelCaseCipher(data) as SurfaceLotData[];
  throw new Error(error);
};

export const getSurfaceLotById = async (
  site_id: number,
  surface_lot_id: number
) => {
  const { data, error } = await FetchClients.tenantsAndSpaces.GET(
    "/{site_id}/surface_lot/{surface_lot_id}",
    {
      params: { path: { site_id, surface_lot_id } },
    }
  );
  if (data) return recursiveCamelCaseCipher(data) as SurfaceLotData;
  throw new Error(error);
};

const getSurfaceLot = async (
  site_id: number,
  surface_lot_id?: number | null
) => {
  if (!surface_lot_id) {
    const [_surfaceLot] = await getSurfaceLots(site_id);
    if (_surfaceLot) return _surfaceLot;
    throw new NotFoundError(`No surface lot set up for site ${site_id}`);
  }
  return getSurfaceLotById(site_id, surface_lot_id);
};

function useQuery(site_id: number, surface_lot_id?: number | null) {
  return useReactQuery({
    queryKey: [SURFACE_LOT_QUERY_KEY, site_id, surface_lot_id],
    queryFn: () => getSurfaceLot(site_id, surface_lot_id),
    refetchOnWindowFocus: false,
    retry: retryIfNot404,
  });
}

function useQueryAll(site_id: number) {
  return useReactQuery({
    queryKey: [SURFACE_LOT_QUERY_KEY, site_id],
    queryFn: () => getSurfaceLots(site_id),
    refetchOnWindowFocus: false,
  });
}

function usePut(
  site_id: number,
  { invalidateParkingQueries }: { invalidateParkingQueries?: boolean } = {}
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: SurfaceLot) => {
      const { response, error } = await FetchClients.tenantsAndSpaces.PUT(
        "/{site_id}/surface_lot",
        {
          params: { path: { site_id } },
          body: recursiveSnakeCaseCipher(body),
        }
      );
      if (response.ok) return;
      throwServiceError(error);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [SURFACE_LOT_QUERY_KEY, site_id],
      });
      if (invalidateParkingQueries)
        await queryClient.invalidateQueries({
          queryKey: [PARKING_QUERY_KEYS.parking, site_id],
        });
    },
  });
}

function useDelete(
  site_id: number,
  {
    invalidateParkingQueries,
  }: {
    invalidateParkingQueries?: boolean;
  } = {}
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (surface_lot_id: number) => {
      const { response, error } = await FetchClients.tenantsAndSpaces.DELETE(
        "/{site_id}/surface_lot/{surface_lot_id}",
        {
          params: { path: { site_id, surface_lot_id } },
        }
      );
      if (response.ok) return;
      throwServiceError(error);
    },
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: [SURFACE_LOT_QUERY_KEY, site_id],
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [SURFACE_LOT_QUERY_KEY, site_id],
      });
      if (invalidateParkingQueries)
        await queryClient.invalidateQueries({
          queryKey: [PARKING_QUERY_KEYS.parking, site_id],
        });
    },
  });
}
